import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import BooleanRadioButtonGroup from '@components/fields/BooleanRadioButtonGroup';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import TagSelect from '@components/fields/TagSelect';
import Form from '@components/forms/Form';
import {
  AutomationFragment as Automation,
  AutomationCreateDocument,
  AutomationCreateMutation,
  AutomationCreateMutationVariables,
  AutomationUpdateDocument,
  AutomationUpdateMutation,
  AutomationUpdateMutationVariables,
  TagTypeEnum
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForBoolean } from '@utils/optionUtils';

type FormData = {
  live: boolean;
  tags: string[];
  title: string;
};

type Props = {
  autofocus: boolean;
  automation?: Automation;
  onCancel: () => void;
  onError: () => void;
  onSuccess: (worksheetTemplateId: string) => void;
};

const AutomationForm = ({ autofocus, automation, onCancel, onError, onSuccess }: Props) => {
  const intl = useIntl();

  const initialValues: FormData = {
    live: automation?.live ?? false,
    tags: automation?.tags ?? [],
    title: automation?.title ?? ''
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createAutomation, { client }] = useMutation<AutomationCreateMutation, AutomationCreateMutationVariables>(
    AutomationCreateDocument,
    {
      update: () => {
        client.resetStore();
      }
    }
  );

  const [updateAutomation] = useMutation<AutomationUpdateMutation, AutomationUpdateMutationVariables>(
    AutomationUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      live: yup.boolean().defined(),
      tags: yup.array().defined().of(yup.string().defined()),
      title: yup.string().required(intl.formatMessage({ id: 'errors.automation.title.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    if (automation?.id) {
      return updateAutomation({
        variables: {
          ...form.getValues(),
          id: automation.id
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.automationUpdate,
          intl,
          onError,
          onSuccess: () => onSuccess(automation.id),
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.automation'
        });
      });
    }

    return createAutomation({
      variables: {
        ...form.getValues()
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.automationCreate,
        intl,
        onError,
        onSuccess: () => {
          if (response.data?.automationCreate?.automation) {
            onSuccess(response.data.automationCreate.automation.id);
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.automation'
      });
    });
  }, [automation?.id, createAutomation, form, intl, onError, onSuccess, updateAutomation]);

  const booleanOptions = useMemo(() => optionsForBoolean(intl), [intl]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input<FormData>
        autofocus={autofocus}
        control={form.control}
        error={form.formState.errors.title}
        label={intl.formatMessage({ id: 'forms.automation.titleLabel' })}
        name="title"
        required
        spellcheck
      />
      <TagSelect<FormData>
        control={form.control}
        errors={form.formState.errors.tags}
        label={intl.formatMessage({ id: 'forms.automation.tagsLabel' })}
        name="tags"
        tagType={TagTypeEnum.AUTOMATION}
      />
      <div className={automation?.id ? '' : 'ion-hide'}>
        <BooleanRadioButtonGroup
          allowDeselection
          control={form.control}
          error={form.formState.errors.live}
          inTracker={false}
          label={intl.formatMessage({ id: 'forms.automation.liveLabel' })}
          name="live"
          options={booleanOptions}
          small
        />
      </div>
      <FormButtons
        hookForm={form}
        onCancel={onCancel}
        saveKey={automation?.id ? 'dictionary.saveChanges' : `pages.admin.automations.createAutomation`}
      />
    </Form>
  );
};

export default AutomationForm;
